import React, {useCallback, useEffect, useMemo, useState} from "react";
import {IApiUser} from "../../services/nestjs-base-api";
import {Button, Modal, Space, Typography, Tag} from "antd";
import {Utils} from "../../utils/utils";
import {useAuth} from "../../hooks/useAuth";
import {useGetUsersQuery} from "../../services/nestjs-base-api";
import {UserRoleEnum} from "../../enums/UserRoleEnum";
import {GeneralStatusEnum} from "../../enums/GeneralStatusEnum";
import OrganisationUserUpdate from "./OrganisationUserUpdate";
import OrganisationUserCreate from "./OrganisationUserCreate";
import { ResponsiveTable } from '../../admin/components/ResponsiveTable';

interface IProps {
}

const OrganisationUsers: React.FunctionComponent<IProps> = (props) => {
    const { user, logout } = useAuth();
    const [apiUser, setApiUser] = useState<IApiUser | undefined>(undefined);
    const [createApiUserOpen, setCreateApiUserOpen] = useState<boolean>(false);
    const {data, refetch, error} = useGetUsersQuery({jwtToken: user.jwtToken}, {pollingInterval: 600000});

    useEffect(() => {
        if (error) {
            if ('status' in error) {
                if (error.status === 401) {
                    logout();
                }
            }
        }
    }, [error, logout]);

    const apiUsers = useMemo(() => {
        return data ? data as unknown as IApiUser[] : undefined;
    }, [data]);

    const onUpdateClose = useCallback(() => {
        setApiUser(undefined);
        refetch();
    }, [refetch]);

    const onCreateClose = useCallback(() => {
        setCreateApiUserOpen(false);
        refetch();
    }, [refetch]);

    const onEditClicked = useCallback((id: number) => {
        setApiUser(apiUsers?.find((user) => user.id === id));
    }, [apiUsers]);

    const onCreateClicked = useCallback(() => {
        setCreateApiUserOpen(true);
    }, []);

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: ((_: string, row: IApiUser) => {
                if (row.firstName && row.lastName) {
                    return `${row.firstName} ${row.lastName}`;
                } else if (row.firstName) {
                    return row.firstName;
                } else if (row.lastName) {
                    return row.lastName;
                }
                return '-';
            })
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            render: (phoneNumber: string) => phoneNumber || '-'
        }, {
            title: 'Roles',
            dataIndex: 'roles',
            key: 'roles',
            render: ((_: string, row: IApiUser) => {
                const roles = typeof row.roles === 'string' ? JSON.parse(row.roles) : row.roles;

                let returnValue = '';
                let first = true;
                roles.forEach((role: UserRoleEnum) => {
                    if (!first) {
                        returnValue += ", ";
                    }

                    returnValue += Utils.readableUserRole(role);
                    first = false;
                });
                return returnValue;
            })
        }, {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: ((_: string, row: IApiUser) => {
                return Utils.readableGeneralStatus(row.status);
            })
        }, {
            title: 'Actions',
            key: 'actions',
            render: ((_: string, row: IApiUser) => {
                return (
                  <Button onClick={() => onEditClicked(row.id)} >
                    Edit
                  </Button>
                );
            })
        }
    ];

    return (
        <div className="project-tab">
            <Button type='primary' style={{marginBottom: '1rem'}} onClick={onCreateClicked}>
                Add user
            </Button>
            <ResponsiveTable 
                dataSource={apiUsers} 
                columns={columns}
                rowKey="id"
                mobileColumns={[
                    {
                        title: 'User',
                        key: 'user',
                        render: (_, row: IApiUser) => {
                            const name = row.firstName && row.lastName 
                                ? `${row.firstName} ${row.lastName}`
                                : row.firstName || row.lastName || '-';
                            const roles = typeof row.roles === 'string' ? JSON.parse(row.roles) : row.roles;
                            const roleNames = roles.map((role: UserRoleEnum) => Utils.readableUserRole(role)).join(', ');
                            
                            return (
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div>
                                        <strong>{name}</strong>
                                        <Tag color={row.status === GeneralStatusEnum.Active ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                                            {Utils.readableGeneralStatus(row.status)}
                                        </Tag>
                                    </div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        {row.email}
                                    </Typography.Text>
                                    {row.phoneNumber && (
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {row.phoneNumber}
                                        </Typography.Text>
                                    )}
                                    <Typography.Text style={{ fontSize: 12 }}>
                                        Roles: {roleNames}
                                    </Typography.Text>
                                </Space>
                            );
                        },
                    },
                    {
                        title: 'Actions',
                        key: 'actions',
                        width: 100,
                        render: (_, row: IApiUser) => (
                            <Button onClick={() => onEditClicked(row.id)} size="small">
                                Edit
                            </Button>
                        ),
                    },
                ]}
            />
            <Modal
                open={!!apiUser}
                onCancel={onUpdateClose}
                centered
                footer={null}
            >
                <OrganisationUserUpdate
                    apiUser={apiUser}
                    onClose={onUpdateClose}
                />

            </Modal>
            <Modal
                open={createApiUserOpen}
                onCancel={onCreateClose}
                centered
                footer={null}
                destroyOnClose={true}
            >
                <OrganisationUserCreate
                    onClose={onCreateClose}
                />
            </Modal>
        </div>
    );
}

export default OrganisationUsers;
